import { create } from '@bufbuild/protobuf';

import {
  CallMemberRole,
  ICallParticipantIdToRole,
  PermissionsTypes,
} from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import { WsRequest } from '@gem/call/WsApi/wsCallsApi/requests/request.types';
import {
  CallMember_Role,
  CallPermission_AccessType,
  CallPermission_RequestStatus,
  CallPermissions,
  CallPermissions_Level,
  MemberWithCustomPermissions,
  ParticipantIdToRole,
  ParticipantIdToRoleSchema,
  PersonalizedCallPermissions,
  PersonalizedCallPermissions_Level,
} from '@gem/call/WsApi/wsCallsApi/types/call/call_model_pb';
import {
  RealtimeCallEvent_MemberPermissionRequestChanged,
  RealtimeCallEvent_MemberPermissionsChanged,
  RealtimeCallEvent_PermissionsChanged,
  RealtimeCallEvent_MemberPermissionRequestCreated,
} from '@gem/call/WsApi/wsCallsApi/types/call/call_realtime_events_pb';
import {
  GetCallPermissionsSuccess,
  GetPermissionRequestsSuccess,
} from '@gem/call/WsApi/wsCallsApi/types/call/call_responses_pb';

import {
  ICallCustomPermissions,
  ICallPermissionRequests,
  ICallPermissions,
  ICallPermissionsChanged,
  ICallPermissionsSettings,
  ICallPersonalizedPermissions,
  ICallUserPermissionRequestCreated,
  ICallUserPermissionsChanged,
  ICallUserPermissionsRequestChanged,
  PermissionRequestAccess,
  PermissionsPersonalizedValue,
  PermissionsRequestStatuses,
  PermissionsValue,
} from '../callPermissionsModel.types';

const callB2bPermissionsLevelResponseMap: Record<CallPermissions_Level, PermissionsValue> = {
  [CallPermissions_Level.EVERYONE]: PermissionsValue.Everyone,
  [CallPermissions_Level.AFTER_APPROVE]: PermissionsValue.AfterApprove,
  [CallPermissions_Level.ADMINS_AND_OWNER]: PermissionsValue.AdminsAndOwner,
};

export const mapDefaultPermissionsResponse = (permissions: CallPermissions): ICallPermissions => {
  return {
    switchOnCamera: callB2bPermissionsLevelResponseMap[permissions.switchOnCamera],
    switchOnMic: callB2bPermissionsLevelResponseMap[permissions.switchOnMic],
    shareScreen: callB2bPermissionsLevelResponseMap[permissions.shareScreen],
  };
};

const callB2bPermissionsPersonalizedLevelResponseMap: Record<
  PersonalizedCallPermissions_Level,
  PermissionsPersonalizedValue
> = {
  [PersonalizedCallPermissions_Level.ALWAYS]: PermissionsPersonalizedValue.Always,
  [PersonalizedCallPermissions_Level.AFTER_APPROVE]: PermissionsPersonalizedValue.AfterApprove,
  [PersonalizedCallPermissions_Level.NEVER]: PermissionsPersonalizedValue.Never,
};

export const mapPersonalizedPermissionsResponse = (
  permissions: PersonalizedCallPermissions
): ICallPersonalizedPermissions => {
  const personalizedPermissions = {} as ICallPersonalizedPermissions;
  if (typeof permissions.switchOnCamera === 'number') {
    personalizedPermissions[PermissionsTypes.SwitchOnCamera] =
      callB2bPermissionsPersonalizedLevelResponseMap[permissions.switchOnCamera];
  }

  if (typeof permissions.switchOnMic === 'number') {
    personalizedPermissions[PermissionsTypes.SwitchOnMic] =
      callB2bPermissionsPersonalizedLevelResponseMap[permissions.switchOnMic];
  }

  if (typeof permissions.shareScreen === 'number') {
    personalizedPermissions[PermissionsTypes.ShareScreen] =
      callB2bPermissionsPersonalizedLevelResponseMap[permissions.shareScreen];
  }

  return personalizedPermissions;
};

export const mapUsersWithCustomPermissionsResponse = (
  permissions: MemberWithCustomPermissions[]
): ICallCustomPermissions[] => {
  return permissions.map((item) => ({
    userId: String(item.memberId),
    personalizedCallPermissions: mapPersonalizedPermissionsResponse(item.personalized!),
  }));
};

export const mapCallB2bPermissionsResponse = (
  permissions: GetCallPermissionsSuccess
): ICallPermissionsSettings => {
  const mappedPermissions: ICallPermissionsSettings = {
    default: mapDefaultPermissionsResponse(permissions.default!),
    personalized: mapPersonalizedPermissionsResponse(permissions.personalized!),
    defaultPersonalized: mapPersonalizedPermissionsResponse(permissions.defaultPersonalized!),
  };

  if ('membersWithCustomPermissions' in permissions) {
    mappedPermissions.usersWithCustomPermissions = mapUsersWithCustomPermissionsResponse(
      permissions.membersWithCustomPermissions
    );
  }

  return mappedPermissions;
};

export const mapCallB2bPermissionsRequestsResponse = (
  permissionsRequests: GetPermissionRequestsSuccess
): ICallPermissionRequests => {
  return {
    switchOnCamera: permissionsRequests.switchOnCamera.map((item) => String(item)),
    switchOnMic: permissionsRequests.switchOnMic.map((item) => String(item)),
    shareScreen: permissionsRequests.shareScreen.map((item) => String(item)),
  };
};

const callB2bPermissionsLevelRequestMap: Record<PermissionsValue, CallPermissions_Level> = {
  [PermissionsValue.Everyone]: CallPermissions_Level.EVERYONE,
  [PermissionsValue.AdminsAndOwner]: CallPermissions_Level.ADMINS_AND_OWNER,
  [PermissionsValue.AfterApprove]: CallPermissions_Level.AFTER_APPROVE,
};

export const mapCallB2bDefaultPermissionsRequest = (
  permissions: Required<ICallPermissions>
): WsRequest<CallPermissions> => {
  return {
    switchOnCamera: callB2bPermissionsLevelRequestMap[permissions[PermissionsTypes.SwitchOnCamera]],
    switchOnMic: callB2bPermissionsLevelRequestMap[permissions[PermissionsTypes.SwitchOnMic]],
    shareScreen: callB2bPermissionsLevelRequestMap[permissions[PermissionsTypes.ShareScreen]],
  };
};

const callB2bPermissionsPersonalizedLevelRequestMap: Record<
  PermissionsPersonalizedValue,
  PersonalizedCallPermissions_Level
> = {
  [PermissionsPersonalizedValue.AfterApprove]: PersonalizedCallPermissions_Level.AFTER_APPROVE,
  [PermissionsPersonalizedValue.Never]: PersonalizedCallPermissions_Level.NEVER,
  [PermissionsPersonalizedValue.Always]: PersonalizedCallPermissions_Level.ALWAYS,
};

export const mapPersonalizedPermissionsRequest = (
  permissions: ICallPersonalizedPermissions
): PersonalizedCallPermissions => {
  const personalizedPermissions = {} as PersonalizedCallPermissions;
  if (permissions[PermissionsTypes.SwitchOnCamera]) {
    personalizedPermissions.switchOnCamera =
      callB2bPermissionsPersonalizedLevelRequestMap[permissions[PermissionsTypes.SwitchOnCamera]];
  }

  if (permissions[PermissionsTypes.SwitchOnMic]) {
    personalizedPermissions.switchOnMic =
      callB2bPermissionsPersonalizedLevelRequestMap[permissions[PermissionsTypes.SwitchOnMic]];
  }

  if (permissions[PermissionsTypes.ShareScreen]) {
    personalizedPermissions.shareScreen =
      callB2bPermissionsPersonalizedLevelRequestMap[permissions[PermissionsTypes.ShareScreen]];
  }

  return personalizedPermissions;
};

export const callB2bPermissionsRequestStatusRequestMap: Record<
  PermissionsRequestStatuses,
  CallPermission_RequestStatus
> = {
  [PermissionsRequestStatuses.Approved]: CallPermission_RequestStatus.APPROVED,
  [PermissionsRequestStatuses.Rejected]: CallPermission_RequestStatus.DECLINED,
};

export const callB2bPermissionsRequestStatusResponseMap: Record<
  CallPermission_RequestStatus,
  PermissionsRequestStatuses
> = {
  [CallPermission_RequestStatus.APPROVED]: PermissionsRequestStatuses.Approved,
  [CallPermission_RequestStatus.DECLINED]: PermissionsRequestStatuses.Rejected,
};

export const callB2bPermissionAccessRequestMap: Record<
  PermissionRequestAccess,
  CallPermission_AccessType
> = {
  [PermissionRequestAccess.Mic]: CallPermission_AccessType.MIC,
  [PermissionRequestAccess.Camera]: CallPermission_AccessType.CAMERA,
  [PermissionRequestAccess.ScreenSharing]: CallPermission_AccessType.SCREEN_SHARING,
};

export const mapB2bPermissionsChangedEvent = (
  data: RealtimeCallEvent_PermissionsChanged
): ICallPermissionsChanged => {
  const changedPermissions = {
    [PermissionsTypes.ShareScreen]:
      typeof data.changed!.shareScreen === 'number'
        ? callB2bPermissionsPersonalizedLevelResponseMap[data.changed!.shareScreen]
        : undefined,
    [PermissionsTypes.SwitchOnMic]:
      typeof data.changed!.switchOnMic === 'number'
        ? callB2bPermissionsPersonalizedLevelResponseMap[data.changed!.switchOnMic]
        : undefined,
    [PermissionsTypes.SwitchOnCamera]:
      typeof data.changed!.switchOnCamera === 'number'
        ? callB2bPermissionsPersonalizedLevelResponseMap[data.changed!.switchOnCamera]
        : undefined,
  };

  return {
    changedPermissions,
    default: mapDefaultPermissionsResponse(data.default!),
    defaultPersonalized: mapPersonalizedPermissionsResponse(data.defaultPersonalized!),
  };
};

export const mapB2bPermissionsMemberChangedEvent = (
  data: RealtimeCallEvent_MemberPermissionsChanged
): ICallUserPermissionsChanged => {
  const personalizedCallPermissions = {
    [PermissionsTypes.ShareScreen]:
      typeof data.changed!.shareScreen === 'number'
        ? callB2bPermissionsPersonalizedLevelResponseMap[data.changed!.shareScreen]
        : undefined,
    [PermissionsTypes.SwitchOnMic]:
      typeof data.changed!.switchOnMic === 'number'
        ? callB2bPermissionsPersonalizedLevelResponseMap[data.changed!.switchOnMic]
        : undefined,
    [PermissionsTypes.SwitchOnCamera]:
      typeof data.changed!.switchOnCamera === 'number'
        ? callB2bPermissionsPersonalizedLevelResponseMap[data.changed!.switchOnCamera]
        : undefined,
  };
  return {
    personalizedCallPermissions,
    userId: data.memberId.toString(),
    purged: data.purged,
  };
};

export const callUserPermissionAccessResponseMap: Record<
  CallPermission_AccessType,
  PermissionRequestAccess
> = {
  [CallPermission_AccessType.MIC]: PermissionRequestAccess.Mic,
  [CallPermission_AccessType.CAMERA]: PermissionRequestAccess.Camera,
  [CallPermission_AccessType.SCREEN_SHARING]: PermissionRequestAccess.ScreenSharing,
};

export const mapB2bPermissionsRequestCreatedEvent = (
  data: RealtimeCallEvent_MemberPermissionRequestCreated
): ICallUserPermissionRequestCreated => {
  return {
    userId: data.memberId.toString(),
    access: callUserPermissionAccessResponseMap[data.access],
  };
};

export const mapB2bPermissionsRequestChangedEvent = (
  data: RealtimeCallEvent_MemberPermissionRequestChanged
): ICallUserPermissionsRequestChanged => {
  const personalizedCallPermissions = {
    [PermissionsTypes.ShareScreen]:
      typeof data.changed!.shareScreen === 'number'
        ? callB2bPermissionsPersonalizedLevelResponseMap[data.changed!.shareScreen]
        : undefined,
    [PermissionsTypes.SwitchOnMic]:
      typeof data.changed!.switchOnMic === 'number'
        ? callB2bPermissionsPersonalizedLevelResponseMap[data.changed!.switchOnMic]
        : undefined,
    [PermissionsTypes.SwitchOnCamera]:
      typeof data.changed!.switchOnCamera === 'number'
        ? callB2bPermissionsPersonalizedLevelResponseMap[data.changed!.switchOnCamera]
        : undefined,
  };
  return {
    personalizedCallPermissions,
    userId: data.memberId.toString(),
    status: callB2bPermissionsRequestStatusResponseMap[data.status],
  };
};

export const participantRolesRequestRoleMap: Record<CallMemberRole, CallMember_Role> = {
  [CallMemberRole.Superuser]: CallMember_Role[CallMemberRole.Superuser],
  [CallMemberRole.Member]: CallMember_Role[CallMemberRole.Member],
  [CallMemberRole.Guest]: CallMember_Role[CallMemberRole.Guest],
};

export const mapParticipantRolesRequest = (
  participantIdsToRole: ICallParticipantIdToRole[]
): ParticipantIdToRole[] => {
  return participantIdsToRole.map((item) =>
    create(ParticipantIdToRoleSchema, {
      userId: BigInt(item.userId),
      role: participantRolesRequestRoleMap[item.role],
    })
  );
};
